import { useState, useEffect, useCallback } from "react";
import { FiCheck, FiAlertCircle } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";

export type SaveLabelStatus = "idle" | "saving" | "saved" | "error";

interface SaveLabelIndicatorProps {
	"data-testid"?: string;
	/** How long (ms) the "saved" label stays visible before fading out. Default 3000. */
	displayDuration?: number;
	/** Custom labels per status. */
	labels?: Partial<Record<SaveLabelStatus, string>>;
	/** Current save status — drives what's displayed. */
	status: SaveLabelStatus;
}

const DEFAULT_LABELS: Record<SaveLabelStatus, string> = {
	idle: "",
	saving: "Saving...",
	saved: "Saved",
	error: "Save failed",
};

/**
 * A small inline label that shows save status and auto-hides after a duration.
 *
 * Usage:
 * ```tsx
 * <SaveLabelIndicator status={saveStatus} />
 * ```
 */
export const SaveLabelIndicator: React.FC<SaveLabelIndicatorProps> = ({
	status,
	displayDuration = 3000,
	labels,
	"data-testid": dataTestId = "save-label-indicator",
}) => {
	const mergedLabels = { ...DEFAULT_LABELS, ...labels };
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (status === "idle") {
			setVisible(false);
			return;
		}

		setVisible(true);

		if (status === "saved") {
			const timer = setTimeout(() => setVisible(false), displayDuration);
			return () => clearTimeout(timer);
		}
	}, [status, displayDuration]);

	if (!visible) return null;

	return (
		<span
			className={`inline-flex items-center gap-1.5 text-xs font-medium transition-opacity duration-300 ${status === "error"
					? "text-red-500"
					: status === "saved"
						? "text-blue-600"
						: "text-gray-400"
				}`}
			data-testid={dataTestId}
		>
			{status === "saving" && <Spinner size="xs" color="gray.400" />}
			{status === "saved" && <FiCheck size={12} />}
			{status === "error" && <FiAlertCircle size={12} />}
			{mergedLabels[status]}
		</span>
	);
};

/**
 * Headless hook for imperative save label control.
 *
 * Usage:
 * ```tsx
 * const saveLabel = useSaveLabelIndicator();
 * // on save start: saveLabel.saving()
 * // on success:    saveLabel.saved()
 * // on error:      saveLabel.error()
 * <SaveLabelIndicator status={saveLabel.status} />
 * ```
 */
export const useSaveLabelIndicator = () => {
	const [status, setStatus] = useState<SaveLabelStatus>("idle");

	const saving = useCallback(() => setStatus("saving"), []);
	const saved = useCallback(() => setStatus("saved"), []);
	const error = useCallback(() => setStatus("error"), []);
	const reset = useCallback(() => setStatus("idle"), []);

	return { status, saving, saved, error, reset };
};
